import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject, catchError, finalize, of, takeUntil } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { SkeletonModule } from 'primeng/skeleton';
import { StyleClassModule } from 'primeng/styleclass';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';

import { ClientService, ToastMessageService } from '@fitness-force/api';
import { DataGridComponent, EmptyStateContainerComponent } from '@fitness-force/grid';
import { DateFormatedComponent } from '@fitness-force/grid-utility';
import { ClientInfoComponent } from '../../data-grid/utility/client-info/client-info.component';
import { GuestPassStatusComponent } from '../../data-grid/utility/guest-pass/guest-pass-status/guest-pass-status.component';

@Component({
  selector: 'guest-pass-list-tab',
  templateUrl: './guest-pass-list-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    StyleClassModule,
    SkeletonModule,
    ButtonModule,
    RippleModule,
    TagModule,
    TableModule,
    DataGridComponent,
    TranslateModule,
    AsyncPipe,
    NgClass,
  ],
})
export class GuestPassListTabComponent implements OnInit, OnDestroy {
  private readonly clientService = inject(ClientService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly clientId = input(0);
  readonly tenantId = input(0);
  readonly subscriptionId = input(0);
  readonly showHeader = input(true);

  redemptionData$ = new BehaviorSubject<any[]>([]);
  redemptionTableColumns$ = new BehaviorSubject<any>([]);
  loading = false;
  emptyStateComponent$ = new BehaviorSubject<any>(EmptyStateContainerComponent);

  ngOnInit(): void {
    this.loadRedemptions();
    this.setTableColumns();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadRedemptions(): void {
    const clientId = this.clientId();
    if (!clientId) return;

    this.loading = true;
    this.cd.markForCheck();
    const subscriptionId = this.subscriptionId() || undefined;

    this.clientService.getGuestPassRedemptions(clientId, subscriptionId)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.toastMessageService.showError(error);
          return of({ items: [] });
        }),
        finalize(() => {
          this.loading = false;
          this.cd.detectChanges();
        })
      )
      .subscribe({
        next: (response: any) => {
          const items = (response?.items ?? []).map((item: any) => ({
            ...item,
            client_info: item.guest_details,
          }));
          this.redemptionData$.next(items);
        }
      });
  }

  setTableColumns(): void {
    this.translate.get([
      'GUEST_PASS.LIST.CHECKED_IN',
      'GUEST_PASS.LIST.GUEST_CLIENT',
      'GUEST_PASS.LIST.STATUS',
      'GUEST_PASS.LIST.SCHEDULED_ON',
    ]).pipe(takeUntil(this.destroy$))
      .subscribe((translations) => {
        this.redemptionTableColumns$.next([
          {
            title: translations['GUEST_PASS.LIST.SCHEDULED_ON'],
            classes: 'py-3 text-600 w-12rem white-space-nowrap',
            sortable: true,
            filterable: true,
            selectable: false,
            dataProperty: 'scheduled_on',
            type: 'component',
            data: {
              name: DateFormatedComponent,
              additionData: true,
              aData: { KeyName: 'scheduled_on', format: 'dd MMM yyyy' },
            },
          },
          {
            title: translations['GUEST_PASS.LIST.GUEST_CLIENT'],
            classes: 'py-3 text-600 w-12rem white-space-nowrap',
            sortable: true,
            filterable: true,
            selectable: false,
            type: 'component',
            data: {
              name: ClientInfoComponent,
            },
          },
          {
            title: translations['GUEST_PASS.LIST.STATUS'],
            classes: 'py-3 text-600 w-10rem white-space-nowrap',
            sortable: true,
            filterable: true,
            selectable: false,
            dataProperty: 'status',
            type: 'component',
            data: {
              name: GuestPassStatusComponent,
              additionData: true,
              aData: { fetch_key: 'status' },
            },
          },
          {
            title: translations['GUEST_PASS.LIST.CHECKED_IN'],
            classes: 'py-3 text-600 w-12rem white-space-nowrap',
            sortable: true,
            filterable: true,
            selectable: false,
            dataProperty: 'checked_in_datetime',
            type: 'component',
            data: {
              name: DateFormatedComponent,
              additionData: true,
              aData: { KeyName: 'checked_in_datetime', format: 'dd MMM yyyy HH:mm' },
            },
          }
          
        ]);
      });
  }

  handleEvent(_event: any): void {
    // grid event handler
  }

  handleSelectAll(_selection: any): void {
    // selection handler
  }
}
